import { queryKeys } from '@/lib/query-keys';
import { simCardClient } from '@/lib/api/ts-rest';
import type { SimCard } from '@octave/contracts';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useDefaultSite } from './useDefaultSite';
import { useCurrentOrganization } from './useOrganization';

export const useSimCard = (id: string | undefined) => {
  return useQuery<SimCard, Error>({
    queryKey: queryKeys.simCards.detail(id),
    queryFn: async () => {
      const res = await simCardClient.getSimCard({
        params: { simCardId: id! },
      });
      if (res.status !== 200) throw new Error('Failed to load SIM card');
      return res.body;
    },
    enabled: !!id,
  });
};

export const useSimCards = (operator: string) => {
  const { data: organization } = useCurrentOrganization();

  return useQuery<SimCard[], Error>({
    queryKey: queryKeys.simCards.byOperator(operator),
    queryFn: async () => {
      if (!organization?.id) {
        throw new Error('Organization not loaded');
      }

      const res = await simCardClient.listFreeSimCards({
        params: { organizationId: organization.id },
        query: { operator },
      });
      if (res.status !== 200) throw new Error('Failed to load SIM cards');
      return res.body;
    },
    enabled: !!organization?.id,
  });
};

interface UseSimCardsStockParams {
  page: number;
  limit: number;
  filters?: {
    status?: string[];
    operator?: string[];
  };
  searchText?: string;
  siteId?: string;
}

interface SimCardsStockResponse {
  simCards: SimCard[];
  pageInfo: {
    nextPage: number | null;
    previousPage: number | null;
    count: number;
    nbPage: number;
    page: number;
  };
}

export const useSimCardsStock = (params: UseSimCardsStockParams) => {
  const { data: organization } = useCurrentOrganization();
  const { currentSite } = useDefaultSite();
  const { page, limit, filters, searchText } = params;

  // Fetch all SIM cards from backend
  const {
    data: allSimCards,
    isLoading,
    isFetching,
  } = useQuery<SimCard[], Error>({
    queryKey: queryKeys.simCards.stock(organization?.id, filters?.operator?.[0]),
    queryFn: async () => {
      if (!organization?.id) {
        throw new Error('Organization not loaded');
      }

      const res = await simCardClient.listFreeSimCards({
        params: { organizationId: organization.id },
        query: { operator: filters?.operator?.[0] },
      });
      if (res.status !== 200) throw new Error('Failed to load SIM cards');
      return res.body;
    },
    enabled: !!organization?.id,
  });

  // Filter and paginate on the client side
  const result = useMemo<SimCardsStockResponse | undefined>(() => {
    if (!allSimCards) return undefined;

    // Le site courant (centre de facturation) sélectionné dans la top bar
    // expose au moins un `name` qui correspond à `sim.owner.name`
    type SiteWithName = { name?: string | null };
    const currentSiteName =
      (currentSite as SiteWithName | null | undefined)?.name ?? undefined;

    // Filter by status
    let filtered = allSimCards;
    if (filters?.status && filters.status.length > 0) {
      filtered = filtered.filter((sim) => filters.status!.includes(sim.status));
    }

    // Filter by operator (déjà appliqué côté backend, on double-check)
    if (filters?.operator && filters.operator.length > 0) {
      filtered = filtered.filter(
        (sim) =>
          sim.operatorLabel !== null &&
          filters.operator!.includes(sim.operatorLabel)
      );
    }

    // Filter by search text (serialNumber / iccid)
    if (searchText && searchText.trim()) {
      const search = searchText.trim().toLowerCase();
      filtered = filtered.filter(
        (sim) =>
          sim.serialNumber?.toLowerCase().includes(search) ||
          sim.iccid?.toLowerCase().includes(search)
      );
    }

    // Filter par centre de facturation (top bar) :
    // - si un centre est sélectionné, on ne garde que les SIM dont owner.name correspond
    // - sinon, on garde toutes les SIM de l'organisation
    if (currentSiteName) {
      filtered = filtered.filter((sim) => sim.owner?.name === currentSiteName);
    }

    // Calculate pagination
    const count = filtered.length;
    const nbPage = Math.ceil(count / limit);
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedSimCards = filtered.slice(startIndex, endIndex);

    return {
      simCards: paginatedSimCards,
      pageInfo: {
        nextPage: page < nbPage ? page + 1 : null,
        previousPage: page > 1 ? page - 1 : null,
        count,
        nbPage,
        page,
      },
    };
  }, [allSimCards, filters, page, limit, currentSite, searchText]);

  return {
    data: result,
    isLoading,
    isFetching,
  };
};
